import { Plural, Trans, useLingui } from '@lingui/react/macro';

import Accordion from '@/design_system/Accordion';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconInvoice from '@/icons/Invoice.svg';
import { getAmountWithQuantity, useGetInvoiceLineName } from '@/models/invoiceLine';
import { RequestWithRelations } from '@/models/request';
import {
  getRequestAmountPaid,
  getRequestBalanceDue,
  isClientRefundTransaction,
  partitionClientPaymentTransactions,
  TransactionFromGetClientPaymentTransactions,
} from '@/models/transaction';
import { formatDate } from '@/utils/date';
import { getConversionPrices } from '@/utils/price';
import useViewPort from '@/utils/useViewport';

import { AmountPaidWithDetailsTooltipWithConversions } from '../AmountPaidWithDetails/AmountPaidWithDetails';
import { BalanceWithDetailsTooltipWithConversions } from '../BalanceWithDetails/BalanceWithDetails';
import { TransactionMethod, TransactionMethodIcon } from '../TransactionMethod';
import { TransactionPriceWithDetailsTooltipWithConversions } from '../TransactionPriceWithDetails/TransactionPriceWithDetails';

const TRUNCATED_REFERENCE_LENGTH = 18;

const formatTransactionReference = (reference: string) => {
  if (reference.length <= TRUNCATED_REFERENCE_LENGTH) {
    return reference;
  }

  return `${reference.slice(0, 8)}...${reference.slice(-6)}`;
};

export const ClientPaymentSection = ({
  request,
  transactions,
  canViewTransactions,
  openByDefault = true,
  isClientView = false,
}: {
  request: RequestWithRelations;
  transactions: TransactionFromGetClientPaymentTransactions[];
  canViewTransactions: boolean;
  openByDefault?: boolean;
  isClientView?: boolean;
}) => {
  const { t } = useLingui();
  const amountPaid = getRequestAmountPaid(transactions);
  const { balance, isRefund } = getRequestBalanceDue(request, amountPaid);
  const { payments, refunds } = partitionClientPaymentTransactions(transactions);
  const dueAmount = request.organizationPrice?.amountPerCurrency?.[0];
  const { isMobile } = useViewPort();

  const balanceLabel = isRefund
    ? t({ id: 'request.client-payment.amount-to-refund', message: 'Amount to refund' })
    : t({ id: 'request.client-payment.balance-due', message: 'Balance due' });

  const amountPaidLabel =
    amountPaid && amountPaid.amount < 0
      ? t({ id: 'request.client-payment.amount-refunded', message: 'Amount refunded' })
      : t({ id: 'request.client-payment.amount-paid', message: 'Amount paid' });

  return (
    <Accordion
      openByDefault={openByDefault}
      trigger={
        <Stack row alignItems="center" gap="0.25rem" flexWrap="nowrap">
          {!!isMobile && <IconInvoice />}
          <Paragraph weight="medium">
            {isClientView ? (
              <Trans id="article.step.payment">Payment</Trans>
            ) : (
              <Trans id="request.client-payment.title">Client payment</Trans>
            )}
          </Paragraph>
        </Stack>
      }
    >
      <Stack gap="0.75rem">
        {balance && dueAmount && (
          <Stack ariaLabel={balanceLabel}>
            <Paragraph color="secondary" size="200">
              {balanceLabel}
            </Paragraph>
            <Paragraph weight="medium">
              <BalanceWithDetailsTooltipWithConversions
                variant={balance.amount > 0 ? 'warning' : 'primary'}
                balance={{
                  amount: balance.amount,
                  currency: balance.currency,
                  conversions: balance.conversions ?? [],
                }}
                amountDue={{
                  amount: dueAmount.amount,
                  currency: dueAmount.currency,
                  conversions: getConversionPrices(dueAmount).map((conversion) => ({
                    amount: conversion.amount.amount,
                    currency: conversion.amount.currency,
                    rate: conversion.rate,
                  })),
                }}
                amountPaid={{
                  amount: amountPaid?.amount ?? 0,
                  currency: amountPaid?.currency ?? dueAmount.currency,
                  conversions: amountPaid?.conversions ?? [],
                }}
              />
            </Paragraph>
          </Stack>
        )}

        {!!amountPaid && (
          <Stack ariaLabel={amountPaidLabel}>
            <Paragraph color="secondary" size="200">
              {amountPaidLabel}
            </Paragraph>
            <Paragraph weight="medium">
              <AmountPaidWithDetailsTooltipWithConversions
                amountPaid={{
                  amount: amountPaid.amount,
                  currency: amountPaid.currency,
                  conversions: amountPaid.conversions ?? [],
                }}
                transactions={transactions.map((transaction) => ({
                  amount: transaction.amount,
                  currency: transaction.currency,
                  conversions: transaction.conversions,
                  isRefund: isClientRefundTransaction(transaction),
                }))}
              />
            </Paragraph>
          </Stack>
        )}

        {payments.length > 0 && (
          <Accordion
            openByDefault={false}
            trigger={
              <Paragraph size="200" color="secondary">
                <Plural
                  id="request.client-payment.payments-count"
                  value={payments.length}
                  one="# payment"
                  other="# payments"
                />
              </Paragraph>
            }
          >
            <Stack gap="0.5rem">
              {payments.map((transaction) => (
                <TransactionCard
                  key={transaction.id}
                  transaction={transaction}
                  canViewTransactions={canViewTransactions}
                />
              ))}
            </Stack>
          </Accordion>
        )}

        {refunds.length > 0 && (
          <Accordion
            openByDefault={false}
            trigger={
              <Paragraph size="200" color="secondary">
                <Plural
                  id="request.client-payment.refunds-count"
                  value={refunds.length}
                  one="# refund"
                  other="# refunds"
                />
              </Paragraph>
            }
          >
            <Stack gap="0.5rem">
              {refunds.map((transaction) => (
                <TransactionCard
                  key={transaction.id}
                  transaction={transaction}
                  canViewTransactions={canViewTransactions}
                />
              ))}
            </Stack>
          </Accordion>
        )}
      </Stack>
    </Accordion>
  );
};

const TransactionCard = ({
  transaction,
  canViewTransactions,
}: {
  transaction: TransactionFromGetClientPaymentTransactions;
  canViewTransactions: boolean;
}) => {
  const { t } = useLingui();
  const getInvoiceLineName = useGetInvoiceLineName();

  const receipt = transaction.media[0];
  const date = formatDate(new Date(transaction.startedAt ?? transaction.createdAt), {
    dateStyle: 'medium',
  });

  const priceLines = transaction.invoiceLines.map((invoiceLine) => ({
    label: getInvoiceLineName(invoiceLine),
    amount: getAmountWithQuantity(invoiceLine.price),
    currency: invoiceLine.price.currency,
    conversions: invoiceLine.conversions,
  }));

  return (
    <Stack
      gap="0.5rem"
      padding="0.75rem"
      style={{ borderRadius: '0.5rem', border: '1px solid var(--color-neutral-300)' }}
    >
      <Stack row justifyContent="space-between" alignItems="center" flexWrap="nowrap">
        <Paragraph size="200">{date}</Paragraph>
        <TransactionReference
          transactionId={transaction.id}
          reference={transaction.reference}
          canViewTransactions={canViewTransactions}
        />
      </Stack>

      <Paragraph weight="medium">
        <TransactionPriceWithDetailsTooltipWithConversions
          price={{
            amount: transaction.amount,
            currency: transaction.currency,
            conversions: transaction.conversions,
          }}
          lines={priceLines}
        />
      </Paragraph>

      <Stack row justifyContent="space-between" alignItems="center" flexWrap="nowrap">
        <Stack row alignItems="center" gap="0.25rem" flexWrap="nowrap">
          <TransactionMethodIcon
            transaction={transaction}
            style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary-black)' }}
          />
          <Paragraph color="secondary" size="200">
            <TransactionMethod transaction={transaction} />
          </Paragraph>
        </Stack>
        {!!receipt?.url && (
          <Button
            variant="style-less"
            ariaLabel={t({
              id: 'request.client-payment.download-receipt',
              message: 'Download receipt',
            })}
            href={receipt.url}
            download
          >
            <Stack row alignItems="center" gap="0.2rem" flexWrap="nowrap">
              <IconInvoice
                style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }}
              />
              <Paragraph
                color="secondary"
                size="200"
                style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}
              >
                <Trans id="request.client-payment.receipt">Receipt</Trans>
              </Paragraph>
            </Stack>
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

const TransactionReference = ({
  transactionId,
  reference,
  canViewTransactions,
}: {
  transactionId: string;
  reference: string | null;
  canViewTransactions: boolean;
}) => {
  const { t } = useLingui();
  const transactionUrl = `/accounting/transactions/${transactionId}`;

  if (!reference) {
    if (!canViewTransactions) {
      return null;
    }

    return (
      <Button
        variant="style-less"
        size="small"
        ariaLabel={t({
          id: 'request.client-payment.see-transaction',
          message: 'See transaction',
        })}
        to={transactionUrl}
      >
        <Paragraph color="disabled" size="400">
          <Trans id="request.client-payment.see-transaction">See transaction</Trans>
        </Paragraph>
      </Button>
    );
  }

  const hasTruncatedReference = reference.length > TRUNCATED_REFERENCE_LENGTH;
  const referenceContent = (
    <Paragraph color="disabled" size="400">
      {hasTruncatedReference ? formatTransactionReference(reference) : reference}
    </Paragraph>
  );

  if (!canViewTransactions) {
    if (hasTruncatedReference) {
      return <Tooltip content={reference}>{referenceContent}</Tooltip>;
    }

    return referenceContent;
  }

  if (hasTruncatedReference) {
    return (
      <Tooltip content={reference}>
        <Button variant="style-less" size="small" ariaLabel={reference} to={transactionUrl}>
          {referenceContent}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button variant="style-less" size="small" ariaLabel={reference} to={transactionUrl}>
      {referenceContent}
    </Button>
  );
};
